<div class="main-view space-y-2 md:space-y-4">
  <app-page-surface-header titleIcon="card"
    title="Subscriptions"
    [subtitle]="
      view === 'deleted'
        ? 'Soft-deleted plans — restore or permanently remove'
        : 'Manage standard and custom subscription plans for clients'
    "
    [tabs]="surfaceTabs"
    [activeTabKey]="view"
    tablistLabel="Subscription list"
    (activeTabKeyChange)="onSurfaceTabKey($event)"
  />

  <app-list-module-surface
    [config]="listSurfaceConfig"
    [filters]="listFilters"
    [deletedView]="view === 'deleted'"
    [errorMessage]="errorMessage"
    [loading]="loading"
    [meta]="meta"
    [rowCount]="rows.length"
    (filtersChange)="onListFiltersChange($event)"
    (pageChange)="onListPageChange($event)"
  >
    <div toolbarFiltersExtra class="flex flex-wrap items-center gap-2">
      <label class="sr-only" for="subscriptionTypeFilter">Plan type</label>
      <select
        id="subscriptionTypeFilter"
        class="form-select rounded-full border-gray-200 py-2 pl-3 pr-8 text-sm dark:border-[#191e3a] dark:bg-[#0e1726]"
        [ngModel]="typeFilter"
        (ngModelChange)="onTypeFilterChange($event)"
      >
        <option value="">All types</option>
        <option value="STANDARD">Standard</option>
        <option value="CUSTOM">Custom</option>
      </select>
    </div>

    <ng-container toolbarActions>
      @if (view === 'active' && (P.SUBSCRIPTION_ADD | hasPermission)) {
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
        (click)="openCreateModal()"
      >
        <icon-plus-circle class="size-5" />
        <span>Add subscription</span>
      </button>
      }
    </ng-container>

    <app-subscription-cards
      listTable
      [rows]="rows"
      [view]="view"
      [loading]="loading"
      [emptyMessage]="emptyMessage"
      [openActionMenuKey]="openActionMenuKey"
      (toggleMenu)="toggleActionMenu($event.key, $event.event)"
      (closeMenu)="closeActionMenu()"
      (edit)="openEditModal($event)"
      (delete)="confirmDelete($event)"
      (permanentDelete)="confirmPermanentDelete($event)"
      (restore)="confirmRestore($event)"
    />
  </app-list-module-surface>
</div>

<!-- Create / Edit modal -->
@if (showFormModal) {
<div
  class="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4"
  (click)="closeFormModal()"
  (keydown.escape)="closeFormModal()"
>
  <div
    class="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl dark:bg-[#0e1726]"
    (click)="$event.stopPropagation()"
    (keydown)="$event.stopPropagation()"
  >
    <div
      class="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-[#191e3a]"
    >
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">
        {{ formModalTitle }}
      </h3>
      <button
        type="button"
        (click)="closeFormModal()"
        class="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-100 dark:border-[#191e3a] dark:hover:bg-white/10"
      >
        <icon-x class="size-4" />
      </button>
    </div>

    <form [formGroup]="form" (ngSubmit)="submitForm()" class="space-y-4 px-6 py-5">
      @if (modalError()) {
      <div class="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">
        {{ modalError() }}
      </div>
      }

      <div>
        <label for="sub-name" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Plan name <span class="text-danger">*</span>
        </label>
        <input
          id="sub-name"
          type="text"
          formControlName="subscription_name"
          placeholder="e.g. Enterprise Plan"
          class="form-input w-full rounded-lg"
        />
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <div>
          <label for="sub-type" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Type <span class="text-danger">*</span>
          </label>
          <select id="sub-type" formControlName="subscription_type" class="form-select w-full rounded-lg">
            <option value="STANDARD">Standard</option>
            <option value="CUSTOM">Custom</option>
          </select>
        </div>
        <div>
          <label for="sub-price" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Price
          </label>
          <input
            id="sub-price"
            type="number"
            min="0"
            step="0.01"
            formControlName="subscription_price"
            placeholder="0.00"
            class="form-input w-full rounded-lg"
          />
        </div>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <div>
          <label for="sub-validity" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Validity (days)
          </label>
          <input
            id="sub-validity"
            type="number"
            min="1"
            formControlName="validity_days"
            placeholder="365"
            class="form-input w-full rounded-lg"
          />
        </div>
        <div class="flex items-end pb-1">
          <label class="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" formControlName="is_active" class="form-checkbox rounded" />
            Active
          </label>
        </div>
      </div>

      <div formGroupName="model_counts" class="space-y-3">
        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Models per product
        </p>
        @if (productsLoading) {
        <p class="text-sm text-gray-400">Loading products…</p>
        } @else if (products.length === 0) {
        <p class="text-sm text-gray-400">No active products found.</p>
        } @else {
        <div class="grid gap-4 sm:grid-cols-2">
          @for (product of products; track product.slug) {
          <div>
            <label
              [attr.for]="modelFieldId(product)"
              class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {{ modelFieldLabel(product) }}
            </label>
            <input
              [id]="modelFieldId(product)"
              type="number"
              min="0"
              step="1"
              [formControlName]="product.slug"
              placeholder="0"
              class="form-input w-full rounded-lg"
            />
          </div>
          }
        </div>
        }
      </div>

      @if (isCustomType) {
      <div>
        <label for="sub-tenant" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Clients <span class="text-danger">*</span>
        </label>
        <div
          class="tenant-multiselect relative"
          (click)="$event.stopPropagation()"
          (keydown)="$event.stopPropagation()"
        >
          <button
            type="button"
            id="sub-tenant"
            class="form-select w-full rounded-lg border-gray-200 text-left dark:border-[#191e3a]"
            [attr.aria-expanded]="tenantDropdownOpen"
            aria-controls="subscription-tenant-panel"
            (click)="toggleTenantDropdown($event)"
            (keydown.escape)="tenantDropdownOpen = false"
          >
            <span class="block truncate" [class.text-gray-400]="!selectedTenantsLabel">
              {{ selectedTenantsLabel || 'Select clients…' }}
            </span>
          </button>
          @if (tenantDropdownOpen) {
          <div
            id="subscription-tenant-panel"
            class="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-[#191e3a] dark:bg-[#0e1726]"
          >
            @if (!tenantOptions.length) {
            <div class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No clients available</div>
            } @else {
            @for (t of tenantOptions; track t.id) {
            <label
              class="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 dark:text-white-dark dark:hover:bg-white/5"
              [attr.for]="'sub-tenant-option-' + t.id"
            >
              <input
                type="checkbox"
                [id]="'sub-tenant-option-' + t.id"
                class="form-checkbox rounded border-gray-300 text-primary dark:border-[#191e3a]"
                [checked]="isTenantSelected(t.id)"
                (change)="toggleTenant(t.id)"
              />
              <span class="truncate">{{ tenantOptionLabel(t) }}</span>
            </label>
            }
            }
          </div>
          }
        </div>
        <p class="mt-1 text-xs text-gray-500">
          Custom plans can be assigned to one or more clients.
        </p>
      </div>
      }

      <div class="flex justify-end gap-3 pt-1">
        <button
          type="button"
          (click)="closeFormModal()"
          class="rounded-lg border border-gray-200 px-5 py-2 text-sm font-medium dark:border-[#191e3a]"
        >
          Cancel
        </button>
        <button
          type="submit"
          [disabled]="formLoading()"
          class="btn rounded-full bg-[#303030] px-8 font-semibold text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
        >
          {{ formLoading() ? 'Saving…' : formMode === 'create' ? 'Save' : 'Update' }}
        </button>
      </div>
    </form>
  </div>
</div>
}

<app-delete-confirm-modal
  [open]="showDeleteModal"
  [heading]="deleteModalHeading"
  [entityName]="selected?.subscription_name ?? ''"
  entityNoun="subscription"
  [variant]="deleteVariant"
  [loading]="deleteLoading()"
  [error]="modalError() ?? ''"
  (cancelled)="closeDeleteModal()"
  (confirmed)="submitDelete()"
/>

<app-restore-confirm-modal
  [open]="showRestoreModal"
  heading="Restore subscription?"
  [entityName]="selected?.subscription_name ?? ''"
  activeListLabel="active subscription list"
  [loading]="restoreLoading()"
  (cancelled)="closeRestoreModal()"
  (confirmed)="submitRestore()"
/>
